<template>
  <v-layout column>
    <v-layout>
      <v-flex xs6>
        <v-layout class="my-2 mx-2" column>
          <v-flex class="text-h6 font-weight-bold mb-3">Customer Information</v-flex>
          <v-layout class="mb-2">
            <v-flex xs4>Name:</v-flex>
            <v-flex xs4>{{ info.first_name }} {{ info.last_name }}</v-flex>
          </v-layout>
          <v-layout class="mb-1">
            <v-flex xs4>Mailing Address:</v-flex>
            <v-flex xs4>{{ info.street_address }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4 />
            <v-flex xs4>{{ info.city }}, {{ info.state }} {{ info.postal_code }}</v-flex>
          </v-layout>
          <v-layout class="mb-1">
            <v-flex xs4>Billing Address:</v-flex>
            <v-flex xs4>{{ creditCard.billingAddress }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4 />
            <v-flex xs4>{{ info.city }}, {{ creditCard.billingState }} {{ creditCard.billingZip }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>Phone:</v-flex>
            <v-flex xs4>{{ info.phone_number }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>E-mail:</v-flex>
            <v-flex xs4>{{ info.email }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>{{ setIdTypeName(info.id_type) }}</v-flex>
            <v-flex xs4>{{ info.id_number }}</v-flex>
          </v-layout>
          <v-layout class="mb-2" v-if="info.id_type != 3">
            <v-flex xs4>State ({{ setIdTypeName(info.id_type) }}):</v-flex>
            <v-flex xs4>{{ setStateName(info.id_state) }}</v-flex>
          </v-layout>
          <v-layout class="mb-2" v-if="info.is_business">
            <v-flex xs4>Business Customer:</v-flex>
            <v-flex xs4>Yes</v-flex>
          </v-layout>
          <v-layout class="mb-2" v-if="info.is_business && info.business_name">
            <v-flex xs4>Company Name:</v-flex>
            <v-flex xs4>{{ info.business_name }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>CC Type:</v-flex>
            <v-flex xs4>{{ ccTypeLabel }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>CC Number (last 4):</v-flex>
            <v-flex xs4>{{ last4(creditCard.ccNumber) }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>CC Expiration Date:</v-flex>
            <v-flex xs4>{{ creditCard.ccExpirationMonth }}/{{ creditCard.ccExpirationYear }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>CC CVV:</v-flex>
            <v-flex xs4>{{ creditCard.ccCVV }}</v-flex>
          </v-layout>
        </v-layout>
      </v-flex>
      <v-flex xs6>
        <v-layout column v-if="unit || unitFeatures">
          <v-flex class="text-h6 font-weight-bold my-2">Unit Details</v-flex>
          <v-layout class="my-2">
            <v-flex xs4>Unit Number:</v-flex>
            <v-flex xs4>{{ unit.name }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>Unit Type:</v-flex>
            <v-flex xs4>
              <span>{{ unitFeatures ? unitFeatures.type_name : unit.type_name }}</span>
            </v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>Unit Size:</v-flex>
            <v-flex xs4>{{ unitSize }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>Rate:</v-flex>
            <v-flex xs4>${{ parseFloat(quotedRate).toFixed(2) }}/month</v-flex>
          </v-layout>
          <v-layout class="mb-2" v-if="options.selectedPromotion && promotionOptions">
            <v-flex xs4>Promotion Offered:</v-flex>
            <v-flex xs4>{{ promoText }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>Selected Incentive:</v-flex>
            <v-flex xs4>
              <span v-if="options.giftCardEligible" class="success--text">
                ${{ giftCardAmount }} Amazon.com Gift Card
              </span>
              <span v-else>None</span>
            </v-flex>
          </v-layout>
          <v-layout class="mb-3">
            <v-flex xs4>Features:</v-flex>
            <v-flex xs4 v-if="unitFeatures">
              <div v-for="(feature, i) in unitFeatures.features" :key="i">{{ feature }}</div>
            </v-flex>
            <v-flex xs4 v-else>
              <div v-for="(feature, i) in unit.features" :key="i">{{ feature }}</div>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout class="mt-3 mx-2">
      <v-flex xs6>
        <v-flex class="text-h6 font-weight-bold mb-3">Summary of Charges</v-flex>
        <charge-breakdown :parent="'rentalInfo'" />
      </v-flex>
      <v-flex xs6>
        <v-layout column>
          <v-flex class="text-h6 font-weight-bold mb-3">Account Settings</v-flex>
          <v-layout class="mb-2">
            <v-flex xs4>Selected Move-in Date:</v-flex>
            <v-flex xs4>{{ formatDate(options.moveInDate) }}</v-flex>
          </v-layout>
          <v-layout class="mb-2">
            <v-flex xs4>Automatic Payments:</v-flex>
            <v-flex xs4>
              <span v-if="options.automaticPayments === '1'" class="success--text">Enrolled</span>
              <span v-else class="error--text">Declined</span>
            </v-flex>
          </v-layout>
          <div v-if="options.selectedPromotion && promotionOptions">
            <v-layout class="mb-2">
              <v-flex xs4>Promotion Offered:</v-flex>
              <v-flex xs4>{{ promoText }}</v-flex>
            </v-layout>
            <v-layout class="mb-2">
              <v-flex xs4>Promotion Begins:</v-flex>
              <v-flex xs4>{{ promotionMonth ? promotionMonth : 'Not Applied' }}</v-flex>
            </v-layout>
          </div>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-layout>
</template>

<script>
import ChargeBreakdown from "./ChargeBreakdown";
import { dateMethodMixins } from "@/mixins/dateMethodMixins";
import { determineAmazonGiftCardAmount } from "@/helpers/gift-cards/determine-amazon-gc-amount";
import { mapState } from "vuex";
import states from "@/helpers/states";
import idTypes from "@/helpers/tenant/tenant-id-types";

export default {
  components: { ChargeBreakdown },

  props: {
    currentStep: {
      type: [Number, String],
      default: 1
    },
    payload: {
      type: Object
    },
    promotionMonth: {
      type: String,
      default: ""
    },
    updateNeeded: {
      type: Boolean
    }
  },

  data() {
    return {
      states: states,
      idTypes: idTypes
    };
  },

  mixins: [dateMethodMixins],

  methods: {
    toSelect() {
      this.$emit("toSelect");
    },

    setIdTypeName(idType) {
      let idTypeName = null;
      for (let index in this.idTypes) {
        if (this.idTypes[index].id === idType) {
          idTypeName = this.idTypes[index].name;
          break;
        }
      }
      return idTypeName;
    },

    setStateName(stateId) {
      let stateName = null;
      for (let index in this.states) {
        if (this.states[index].value === stateId) {
          stateName = this.states[index].text;
          break;
        }
      }
      return stateName;
    },

    toLeaseInfo() {
      this.$emit("toLeaseInfo");
    },

    toOptions() {
      this.$emit("toOptions");
    },

    toPayment() {
      this.$emit("toPayment");
    },

    last4(ccNumber) {
      if (ccNumber.length === 19) { // standard card
        return ccNumber.substr(15);
      } else { // amex card
        return ccNumber.substr(13);
      }
    }
  },

  computed: {
    ...mapState({
      creditCard: state => state.tenant.creditCard,
      giftCardTiers: state => state.giftCards.giftCardTiers,
      info: state => state.tenant.info,
      moveInCharges: state => state.facilities.moveInCharges,
      options: state => state.tenant.options,
      quotedRate: state => state.facilities.quotedRate,
      unit: state => state.facilities.unit,
      unitFeatures: state => state.facilities.unitFeatures,
      promotionOptions: state => state.facilities.promotionOptions
    }),

    unitSize() {
      if (this.unitFeatures) {
        return "'" + parseFloat(this.unitFeatures.width) + " x '" + parseFloat(this.unitFeatures.length);
      } else if (this.unit && this.unit.width) {
        return "'" + parseFloat(this.unit.width) + " x '" + parseFloat(this.unit.length);
      } else {
        return "";
      }
    },

    giftCardAmount() {
      if (this.options.giftCardEligible) {
        return determineAmazonGiftCardAmount(this.unit.rate, this.giftCardTiers);
      }

      return 0;
    },

    promoText() {
      const i = Object.keys(this.promotionOptions).findIndex(key => this.promotionOptions[key].id === this.options.selectedPromotion);
      return i > -1 ? this.promotionOptions[Object.keys(this.promotionOptions)[i]].text : "";
    },

    ccTypeLabel() {
      switch (this.creditCard.ccType) {
        case "master-card":
          return "Mastercard";
        case "visa":
          return "Visa";
        case "discover":
          return "Discover";
        case "amex":
          return "American Express";
        default:
          return "";
      }
    }
  }
};
</script>
